import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import VoiceRecorder from '../components/VoiceRecorder';
import { Trash2, FileText, Eye, ArrowLeft, AlertTriangle, Edit, Mail, Printer, Download, Copy, Camera, X, Image as ImageIcon, Sparkles, BookOpen, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PupilLogsPage = () => {
  const { pupilId } = useParams();
  const [pupil, setPupil] = useState(null);
  const [logs, setLogs] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [totalHours, setTotalHours] = useState(0);
  const [skillRatings, setSkillRatings] = useState({});
  const [showSkillsProgress, setShowSkillsProgress] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editUploadedFiles, setEditUploadedFiles] = useState([]);
  const [isEditUploading, setIsEditUploading] = useState(false);
  const [lessonPlans, setLessonPlans] = useState([]);
  const [isGeneratingPlan, setIsGeneratingPlan] = useState(false);
  const [showLessonPlans, setShowLessonPlans] = useState(false);
  const [expandedPlanId, setExpandedPlanId] = useState(null);
  const [editHasVoiceNote, setEditHasVoiceNote] = useState(false);
  const [editLessonSuggestion, setEditLessonSuggestion] = useState('');
  const [isEditGeneratingSuggestion, setIsEditGeneratingSuggestion] = useState(false);
  const [editFormData, setEditFormData] = useState({
    lesson_date: '',
    lesson_hours: 1.0,
    lesson_time: '',
    answers: [],
    skills: '',
    notes: ''
  });

  // Skills list for the traffic-light progress dashboard
  const DRIVING_SKILLS = [
    'Accelerator Control', 'Adverse Weather Driving', 'Angled Starts', 'Anticipation', 'Forward Bay Parking', 'Reverse Bay Parking', 'Blind Spot Checks', 'Braking',
    'Clearance', 'Clutch Control', 'Cockpit Drill', 'Controlled/Emergency Stop','Crossroads',
    'Eco Driving', 'Effective Observation', 'Emerging at Junctions',
    'Following Distance','Reversing with Observation',
    'Gap Selection', 'Gear Changes','Right Turns',
    'Handbrake Control', 'Hazard Perception', 'Hill Start',
    'Independent Driving', 'Independent Driving (Sat Nav)', 'Initial Driving Assessment',
    'Junction Approach', 'Speed Awareness & Limits',
    'Lane Changes', 'Lane Discipline', 'Left Turns',
    'Meeting Traffic', 'Mini Roundabouts', 'Mirrors', 'Motorway Driving', 'Moving Off Safely', 'MSPSL Routine',
    'Narrow Roads', 'Night Driving',
    'One-Way Streets', 'Overtaking',
    'Parallel Parking', 'Pedestrian Crossings', 'Pelican Crossings', 'Planning Ahead', 'Pull Up on the Left', 'Pull Up on the Right & Reverse','Progress',
    'Reading Road Signs', 'Risk Assessment', 'Road Markings', 'Road Positioning', 'Roundabouts', 'Route Planning',
    'Safety Margins', 'School Zones', 'Show Me / Tell Me Questions', 'Signalling', 'Speed Control', 'Spiral Roundabouts', 'Steering',
    'T-Junctions', 'Traffic Calming', 'Traffic Lights',
    'Urban Driving',
    'Vehicle Maintenance', 'Vehicle Safety Checks',
    'Wet Weather Driving',
    'Zebra Crossings','Mock Test',
  ];

  useEffect(() => {
    if (pupilId) {
      fetchPupil();
      fetchPupilLogs();
      fetchQuestions();
      fetchSkillRatings();
      fetchLessonPlans();
    }
  }, [pupilId]); // Fetch functions are stable (defined in component scope)

  const fetchPupil = async () => {
    try {
      const response = await axios.get(`${API}/pupils/${pupilId}`);
      setPupil(response.data);
    } catch (error) {
      console.error('Error fetching pupil:', error);
      toast.error('Failed to load pupil information');
    }
  };

  const fetchPupilLogs = async () => {
    try {
      const response = await axios.get(`${API}/reflection-logs/pupil/${pupilId}`);
      setLogs(response.data);
      
      const loggedHours = response.data.reduce((sum, log) => sum + (log.lesson_hours || 1.0), 0);
      // Include additional hours from pupil profile
      const pupilData = await axios.get(`${API}/pupils/${pupilId}`).catch(() => null);
      const additional = pupilData?.data?.additional_hours || 0;
      setTotalHours(Math.round((loggedHours + additional) * 10) / 10);
    } catch (error) {
      console.error('Error fetching pupil logs:', error);
      toast.error('Failed to load reflection logs');
    }
  };

  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${API}/questions/all`);
      setQuestions(response.data);
    } catch (error) {
      console.error('Error fetching questions:', error);
    }
  };

  const fetchSkillRatings = async () => {
    try {
      const response = await axios.get(`${API}/pupils/${pupilId}/skill-ratings`);
      setSkillRatings(response.data);
    } catch (error) {
      console.error('Error fetching skill ratings:', error);
    }
  };

  const fetchLessonPlans = async () => {
    try {
      const response = await axios.get(`${API}/pupils/${pupilId}/lesson-plans`);
      setLessonPlans(response.data);
      if (response.data.length > 0) setShowLessonPlans(true);
    } catch (error) {
      console.error('Error fetching lesson plans:', error);
    }
  };

  const generateLessonPlan = async () => {
    setIsGeneratingPlan(true);
    try {
      const response = await axios.post(`${API}/pupils/${pupilId}/lesson-plans/generate`);
      setLessonPlans(prev => [response.data, ...prev]);
      setExpandedPlanId(response.data.id);
      setShowLessonPlans(true);
      toast.success('Lesson plan generated successfully');
    } catch (error) {
      console.error('Error generating lesson plan:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate lesson plan');
    } finally {
      setIsGeneratingPlan(false);
    }
  };

  const deleteLessonPlan = async (planId) => {
    if (!window.confirm('Delete this lesson plan?')) return;
    try {
      await axios.delete(`${API}/pupils/${pupilId}/lesson-plans/${planId}`);
      setLessonPlans(prev => prev.filter(p => p.id !== planId));
      toast.success('Lesson plan deleted');
    } catch (error) {
      toast.error('Failed to delete lesson plan');
    }
  };

  const cycleSkillRating = async (skill) => {
    const order = ['none', 'red', 'amber', 'green'];
    const current = skillRatings[skill] || 'none';
    const nextIdx = (order.indexOf(current) + 1) % order.length;
    const next = order[nextIdx];
    
    try {
      await axios.put(`${API}/pupils/${pupilId}/skill-ratings`, { skill, rating: next });
      setSkillRatings(prev => {
        const updated = { ...prev };
        if (next === 'none') { delete updated[skill]; } else { updated[skill] = next; }
        return updated;
      });
    } catch (error) {
      toast.error('Failed to update skill rating');
    }
  };

  const handleDeleteLog = async (logId, lessonDate) => {
    if (!window.confirm(`Are you sure you want to delete this reflection log from ${new Date(lessonDate).toLocaleDateString()}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/reflection-logs/${logId}`);
      toast.success('Reflection log deleted successfully');
      fetchPupilLogs(); // Refresh the list
    } catch (error) {
      console.error('Error deleting reflection log:', error);
      toast.error('Failed to delete reflection log');
    }
  };

  const handleResendEmail = async (logId, lessonDate) => {
    if (!pupil?.email) {
      toast.error('Cannot resend email: Pupil does not have an email address');
      return;
    }

    if (!window.confirm(`Resend reflection log email to ${pupil.name} (${pupil.email}) for lesson on ${new Date(lessonDate).toLocaleDateString()}?`)) {
      return;
    }

    try {
      const response = await axios.post(`${API}/reflection-logs/${logId}/resend-email`);
      toast.success(`Email resent successfully to ${response.data.pupil_email}`);
    } catch (error) {
      console.error('Error resending email:', error);
      toast.error('Failed to resend email: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const handlePrintLog = async (logId, lessonDate) => {
    try {
      toast.loading('Preparing printable file...');
      const response = await axios.get(`${API}/reflection-logs/${logId}/print`);
      const printData = response.data;
      
      // Create printable HTML content
      const htmlContent = `
<!DOCTYPE html>
<html>
<head>
    <title>Reflection Log - ${printData.pupil.name}</title>
    <style>
        body { font-family: Arial, sans-serif; margin: 20px; line-height: 1.6; color: #333; }
        .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 20px; margin-bottom: 20px; }
        .info-section { display: flex; justify-content: space-between; margin-bottom: 20px; }
        .info-box { width: 48%; }
        .question-box { margin: 15px 0; padding: 15px; border-left: 4px solid #007bff; background: #f8f9fa; }
        .skills { margin: 10px 0; }
        .skill-tag { display: inline-block; background: #e9ecef; padding: 4px 8px; margin: 2px; border-radius: 3px; font-size: 12px; }
        h1 { margin: 0; color: #333; }
        h2 { margin: 10px 0 0 0; color: #666; }
        h3 { color: #333; margin-bottom: 10px; }
        .notes { padding: 15px; background: #f0f8ff; border: 1px solid #cce7ff; margin: 20px 0; }
        .footer { margin-top: 30px; padding-top: 20px; border-top: 1px solid #ccc; font-size: 12px; color: #666; text-align: center; }
        @media print {
            body { margin: 0; }
            .info-section { display: block; }
            .info-box { width: 100%; margin-bottom: 20px; }
        }
    </style>
</head>
<body>
    <div class="header">
        <h1>${printData.instructor.school_name}</h1>
        <h2>Driving Lesson Reflection Log</h2>
    </div>
    
    <div class="info-section">
        <div class="info-box">
            <h3>Pupil Information</h3>
            <p><strong>Name:</strong> ${printData.pupil.name}</p>
            <p><strong>Email:</strong> ${printData.pupil.email || 'N/A'}</p>
            <p><strong>Phone:</strong> ${printData.pupil.phone || 'N/A'}</p>
            <p><strong>License:</strong> ${printData.pupil.license_number || 'N/A'}</p>
        </div>
        <div class="info-box">
            <h3>Lesson Information</h3>
            <p><strong>Date:</strong> ${printData.lesson.date}</p>
            <p><strong>Duration:</strong> ${printData.lesson.hours} hour(s)</p>
            <p><strong>Instructor:</strong> ${printData.instructor.name}</p>
            <p><strong>Email:</strong> ${printData.instructor.email}</p>
        </div>
    </div>

    ${printData.lesson.skills && printData.lesson.skills.length > 0 ? `
    <div>
        <h3>Skills Practiced</h3>
        <div class="skills">
            ${printData.lesson.skills.map(skill => `<span class="skill-tag">${skill}</span>`).join('')}
        </div>
    </div>
    ` : ''}

    <div>
        <h3>Reflection Questions & Answers</h3>
        ${printData.answers.map((qa, index) => `
            <div class="question-box">
                <div style="font-weight: bold; margin-bottom: 8px;">Q${index + 1}: ${qa.question_text}</div>
                <div><strong>Answer:</strong> ${qa.answer_text}</div>
            </div>
        `).join('')}
    </div>

    ${printData.lesson.notes ? `
    <div class="notes">
        <h3>Instructor Notes</h3>
        <p>${printData.lesson.notes}</p>
    </div>
    ` : ''}

    <div class="footer">
        <p>Generated on: ${new Date().toLocaleString()}</p>
    </div>

    <script>
        // Auto-print when page loads
        window.onload = function() {
            setTimeout(function() {
                window.print();
            }, 1000);
        };
    </script>
</body>
</html>`;

      // Create and download HTML file
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${printData.pupil.name.replace(/[^a-zA-Z0-9]/g, '_')}_ReflectionLog_${new Date(lessonDate).toISOString().split('T')[0]}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.dismiss();
      toast.success('Printable HTML file downloaded! Open it and press Ctrl+P to print.');
    } catch (error) {
      console.error('Error creating printable file:', error);
      toast.dismiss();
      toast.error('Failed to create printable file: ' + error.message);
    }
  };

  const handleDownloadPDF = async (logId, lessonDate) => {
    try {
      toast.loading('Generating PDF...');
      
      // Use server-side PDF generation to bypass browser sandbox restrictions
      const response = await axios.get(`${API}/reflection-logs/${logId}/download-pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from Content-Disposition header or create default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `ReflectionLog_${new Date(lessonDate).toISOString().split('T')[0]}.pdf`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) {
          filename = match[1].replace(/"/g, '');
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.dismiss();
      toast.success(`PDF downloaded: ${filename}`);
      
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.dismiss();
      toast.error(`Failed to download PDF: ${error.message}`);
    }
  };

  const handleClearAllLogs = async () => {
    if (!window.confirm(`⚠️ WARNING: This will permanently delete ALL reflection logs and clear all hours for ${pupil?.name}. This action cannot be undone. Are you absolutely sure?`)) {
      return;
    }
    
    // Double confirmation for such a destructive action
    if (!window.confirm(`This will delete ${logs.length} reflection logs totaling ${totalHours} hours. Type "DELETE" in the next dialog to confirm.`) || 
        !window.prompt('Type "DELETE" to confirm clearing all logs:').toUpperCase().includes('DELETE')) {
      toast.info('Clear all logs cancelled');
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/pupils/${pupilId}/logs`);
      toast.success(`All reflection logs cleared for ${pupil?.name} (${response.data.deleted_count} logs deleted)`);
      fetchPupilLogs(); // Refresh the list
    } catch (error) {
      console.error('Error clearing all logs:', error);
      toast.error('Failed to clear all logs');
    }
  };

  const getQuestionText = (questionId, answer) => {
    // Prefer stored snapshot, fall back to live lookup for old logs
    if (answer?.question_text) return answer.question_text;
    const question = questions.find(q => q.id === questionId);
    return question ? question.text : 'Question no longer available';
  };

  const getQuestion = (questionId) => {
    return questions.find(q => q.id === questionId);
  };

  const renderAnswerDisplay = (answer) => {
    const question = getQuestion(answer.question_id);
    
    if (!question) {
      return <p className="text-gray-600">{answer.answer_text}</p>;
    }

    if (question.question_type === 'scale') {
      const scaleValue = parseInt(answer.answer_text);
      const scaleRange = question.scale_max - question.scale_min + 1;
      const percentage = ((scaleValue - question.scale_min) / (question.scale_max - question.scale_min)) * 100;
      
      return (
        <div className="space-y-2">
          <div className="flex items-center space-x-3">
            <span className="text-2xl font-bold text-blue-600">{scaleValue}</span>
            <span className="text-sm text-gray-600">out of {question.scale_max}</span>
            <Badge variant={scaleValue >= question.scale_max * 0.8 ? 'default' : scaleValue >= question.scale_max * 0.6 ? 'secondary' : 'destructive'}>
              {scaleValue >= question.scale_max * 0.8 ? 'Excellent' : scaleValue >= question.scale_max * 0.6 ? 'Good' : 'Needs Improvement'}
            </Badge>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full" 
              style={{ width: `${percentage}%` }}
            ></div>
          </div>
        </div>
      );
    }

    return <p className="text-gray-600">{answer.answer_text}</p>;
  };

  const handleEditLog = (log) => {
    setEditingLog(log);
    const lessonDate = new Date(log.lesson_date);
    setEditFormData({
      lesson_date: lessonDate.toISOString().split('T')[0],
      lesson_hours: log.lesson_hours || 1.0,
      lesson_time: log.lesson_time || '',
      answers: log.answers.map(a => ({ question_id: a.question_id, answer_text: a.answer_text })),
      skills: (log.skills || []).join(', '),
      notes: log.notes || ''
    });
    // Load existing attachments as edit files (no preview — they load via API)
    const existing = (log.attachments || []).map(att => ({
      id: att.id,
      filename: att.original_filename,
      size: att.size,
      previewUrl: null,
      existing: true
    }));
    setEditUploadedFiles(existing);
    setEditHasVoiceNote(false);
    setEditLessonSuggestion('');
    setIsEditDialogOpen(true);
  };

  const handleEditAnswerChange = (questionId, answer) => {
    setEditFormData(prev => ({
      ...prev,
      answers: prev.answers.map(a =>
        a.question_id === questionId
          ? { ...a, answer_text: answer }
          : a
      )
    }));
  };

  const handleEditFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setIsEditUploading(true);
    const newUploads = [];
    for (const file of files) {
      if (!file.type.startsWith('image/')) { toast.error(`${file.name} is not an image`); continue; }
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} exceeds 10MB limit`); continue; }
      try {
        const fd = new FormData();
        fd.append('file', file);
        const response = await axios.post(`${API}/attachments/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        newUploads.push({
          id: response.data.id,
          filename: response.data.filename,
          size: response.data.size,
          previewUrl: URL.createObjectURL(file),
          existing: false
        });
      } catch (error) { toast.error(`Failed to upload ${file.name}`); }
    }
    setEditUploadedFiles(prev => [...prev, ...newUploads]);
    setIsEditUploading(false);
    e.target.value = '';
  };

  const removeEditFile = (fileId) => {
    setEditUploadedFiles(prev => {
      const file = prev.find(f => f.id === fileId);
      if (file?.previewUrl) URL.revokeObjectURL(file.previewUrl);
      return prev.filter(f => f.id !== fileId);
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingLog) return;

    const validAnswers = editFormData.answers.filter(a => a.answer_text.trim() !== '');
    if (validAnswers.length === 0) {
      toast.error('Please provide at least one answer');
      return;
    }

    try {
      const submitData = {
        lesson_date: new Date(editFormData.lesson_date).toISOString(),
        lesson_hours: editFormData.lesson_hours,
        lesson_time: editFormData.lesson_time || null,
        answers: validAnswers,
        skills: editFormData.skills.trim() ? [editFormData.skills.trim()] : null,
        notes: editFormData.notes || null,
        attachments: editUploadedFiles.length > 0 ? editUploadedFiles.map(f => f.id) : null
      };

      await axios.put(`${API}/reflection-logs/${editingLog.id}`, submitData);
      // Clean up preview URLs
      editUploadedFiles.forEach(f => { if (f.previewUrl) URL.revokeObjectURL(f.previewUrl); });
      setEditUploadedFiles([]);
      toast.success('Reflection log updated successfully');
      setIsEditDialogOpen(false);
      setEditingLog(null);
      setEditHasVoiceNote(false);
      setEditLessonSuggestion('');
      fetchPupilLogs();
    } catch (error) {
      console.error('Error updating reflection log:', error);
      toast.error('Failed to update reflection log: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDuplicateLog = async (log) => {
    try {
      const today = new Date().toISOString();
      const submitData = {
        pupil_id: log.pupil_id,
        lesson_date: today,
        lesson_hours: log.lesson_hours || 1.0,
        lesson_time: log.lesson_time || null,
        answers: log.answers.map(a => ({ question_id: a.question_id, answer_text: a.answer_text })),
        skills: log.skills && log.skills.length > 0 ? log.skills : null,
        notes: log.notes || null,
        send_email: false
      };

      const response = await axios.post(`${API}/reflection-logs`, submitData);
      toast.success('Log duplicated with today\'s date — click Edit to adjust');
      fetchPupilLogs();

      // Auto-open edit dialog for the new log so instructor can tweak
      if (response.data) {
        handleEditLog(response.data);
      }
    } catch (error) {
      console.error('Error duplicating log:', error);
      toast.error('Failed to duplicate log: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Image component that loads from API as blob
  const LogImage = ({ attachmentId, filename }) => {
    const [src, setSrc] = React.useState(null);
    React.useEffect(() => {
      let url;
      axios.get(`${API}/attachments/${attachmentId}`, { responseType: 'blob' })
        .then(res => {
          url = URL.createObjectURL(res.data);
          setSrc(url);
        })
        .catch(() => setSrc(null));
      return () => { if (url) URL.revokeObjectURL(url); };
    }, [attachmentId]);
    
    if (!src) return <div className="w-full h-28 bg-gray-100 rounded-lg animate-pulse" />;
    return (
      <a href={src} target="_blank" rel="noopener noreferrer" className="block">
        <img src={src} alt={filename} className="w-full h-28 object-cover rounded-lg border border-gray-200 hover:opacity-90 transition-opacity" />
      </a>
    );
  };

  const EditLogImage = ({ attachmentId }) => {
    const [src, setSrc] = React.useState(null);
    React.useEffect(() => {
      let url;
      axios.get(`${API}/attachments/${attachmentId}`, { responseType: 'blob' })
        .then(res => { url = URL.createObjectURL(res.data); setSrc(url); })
        .catch(() => setSrc(null));
      return () => { if (url) URL.revokeObjectURL(url); };
    }, [attachmentId]);
    if (!src) return <div className="w-full h-24 bg-gray-100 animate-pulse" />;
    return <img src={src} alt="" className="w-full h-24 object-cover" />;
  };

  if (!pupil) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="animate-pulse">
          <div className="flex items-center mb-8">
            <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded mr-4"></div>
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-64"></div>
          </div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-center mb-8">
        <Link to="/pupils">
          <Button variant="ghost" size="sm" className="mr-4">
            <ArrowLeft size={16} className="mr-1" />
            Back to Pupils
          </Button>
        </Link>
        <div className="flex-1">
          <h2 className="text-3xl font-bold text-slate-800 dark:text-slate-200">{pupil.name}'s Reflection Logs</h2>
          <div className="flex items-center space-x-4 mt-1">
            <p className="text-gray-600">
              {pupil.email && `${pupil.email} • `}
              {pupil.license_number && `License: ${pupil.license_number} • `}
              {logs.length} lesson{logs.length !== 1 ? 's' : ''} recorded
            </p>
            <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
              {totalHours}h total
            </Badge>
            {pupil.email && (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                📧 Auto-emailed
              </Badge>
            )}
          </div>
        </div>
        
        {logs.length > 0 && (
          <Button 
            variant="destructive" 
            size="sm" 
            onClick={handleClearAllLogs}
            className="ml-4"
          >
            <AlertTriangle size={16} className="mr-1" />
            Clear All Logs
          </Button>
        )}
      </div>

      {/* Skills Progress Dashboard */}
      <Card className="mb-6">
        <CardHeader className="cursor-pointer" onClick={() => setShowSkillsProgress(!showSkillsProgress)}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CardTitle className="text-xl text-slate-800">Skills Progress</CardTitle>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-green-500 inline-block" /> {Object.values(skillRatings).filter(r => r === 'green').length}</span>
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-amber-400 inline-block" /> {Object.values(skillRatings).filter(r => r === 'amber').length}</span>
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-500 inline-block" /> {Object.values(skillRatings).filter(r => r === 'red').length}</span>
              </div>
            </div>
            <Eye size={18} className={`text-green-600 transition-transform ${showSkillsProgress ? 'rotate-180' : ''}`} />
          </div>
        </CardHeader>
        {showSkillsProgress && (
          <CardContent>
            <div className="flex items-center gap-4 mb-4 text-sm text-gray-600">
              <span>Click a skill to cycle:</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-gray-300 inline-block" /> Not started</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-500 inline-block" /> Introduced</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-amber-400 inline-block" /> Progressing</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-green-500 inline-block" /> Satisfactory</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {DRIVING_SKILLS.map((skill) => {
                const rating = skillRatings[skill] || 'none';
                const bgMap = { none: 'bg-gray-100 border-gray-200 text-gray-600', red: 'bg-red-50 border-red-300 text-red-800', amber: 'bg-amber-50 border-amber-300 text-amber-800', green: 'bg-green-50 border-green-400 text-green-800' };
                const dotMap = { none: 'bg-gray-300', red: 'bg-red-500', amber: 'bg-amber-400', green: 'bg-green-500' };
                return (
                  <button
                    key={skill}
                    data-testid={`skill-rating-${skill.replace(/\s+/g, '-').toLowerCase()}`}
                    onClick={() => cycleSkillRating(skill)}
                    className={`flex items-center gap-2 p-2.5 rounded-lg border text-left text-sm font-medium transition-all hover:shadow-sm ${bgMap[rating]}`}
                  >
                    <span className={`w-3.5 h-3.5 rounded-full flex-shrink-0 ${dotMap[rating]}`} />
                    <span className="truncate">{skill}</span>
                  </button>
                );
              })}
            </div>
          </CardContent>
        )}
      </Card>

      {/* AI Lesson Plans Section */}
      <Card className="mb-6" data-testid="lesson-plans-section">
        <CardHeader className="cursor-pointer" onClick={() => setShowLessonPlans(!showLessonPlans)}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CardTitle className="text-xl text-slate-800 dark:text-slate-200 flex items-center gap-2">
                <BookOpen size={20} className="text-indigo-600" />
                AI Lesson Plans
              </CardTitle>
              {lessonPlans.length > 0 && (
                <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">
                  {lessonPlans.length} saved
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                data-testid="generate-lesson-plan-btn"
                disabled={isGeneratingPlan}
                onClick={(e) => {
                  e.stopPropagation();
                  generateLessonPlan();
                }}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                {isGeneratingPlan ? (
                  <><Loader2 size={16} className="mr-1 animate-spin" /> Generating...</>
                ) : (
                  <><Sparkles size={16} className="mr-1" /> Suggest Next Lesson</>
                )}
              </Button>
              {showLessonPlans ? <ChevronUp size={18} className="text-indigo-600" /> : <ChevronDown size={18} className="text-indigo-600" />}
            </div>
          </div>
        </CardHeader>
        {showLessonPlans && (
          <CardContent>
            {lessonPlans.length === 0 && !isGeneratingPlan && (
              <p className="text-gray-500 text-center py-4">No lesson plans yet. Click "Suggest Next Lesson" to generate one based on skill progress.</p>
            )}
            {isGeneratingPlan && lessonPlans.length === 0 && (
              <div className="flex items-center justify-center py-8 text-indigo-600">
                <Loader2 size={24} className="animate-spin mr-2" />
                <span>Analysing skills and generating lesson plan...</span>
              </div>
            )}
            <div className="space-y-4">
              {lessonPlans.map((plan) => (
                <div key={plan.id} data-testid={`lesson-plan-${plan.id}`} className="border rounded-lg overflow-hidden">
                  <div
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                    onClick={() => setExpandedPlanId(expandedPlanId === plan.id ? null : plan.id)}
                  >
                    <div className="flex items-center gap-2">
                      <Sparkles size={14} className="text-indigo-500" />
                      <span className="font-medium text-sm text-gray-700 dark:text-gray-200">
                        Plan — {new Date(plan.created_at).toLocaleDateString()} at {new Date(plan.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </span>
                      {plan.focus_areas && plan.focus_areas.length > 0 && (
                        <div className="hidden sm:flex gap-1 ml-2">
                          {plan.focus_areas.slice(0, 3).map((area, i) => (
                            <Badge key={i} variant="outline" className="text-xs bg-red-50 text-red-600 border-red-200">{area}</Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`delete-plan-${plan.id}`}
                        onClick={(e) => { e.stopPropagation(); deleteLessonPlan(plan.id); }}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0"
                      >
                        <Trash2 size={14} />
                      </Button>
                      {expandedPlanId === plan.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </div>
                  </div>
                  {expandedPlanId === plan.id && (
                    <div className="p-4 border-t prose prose-sm max-w-none dark:prose-invert">
                      <div className="whitespace-pre-wrap text-gray-700 dark:text-gray-300 text-sm leading-relaxed" data-testid={`plan-text-${plan.id}`}>
                        {plan.plan_text}
                      </div>
                      {plan.skills_to_practice && plan.skills_to_practice.length > 0 && (
                        <div className="mt-3 pt-3 border-t">
                          <span className="text-xs font-semibold text-gray-500 uppercase">Skills to work on:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {plan.skills_to_practice.map((skill, i) => (
                              <Badge key={i} variant="outline" className="text-xs">{skill}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        )}
      </Card>

      <div className="space-y-6">
        {logs.map((log) => (
          <Card key={log.id} className="card-hover">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-xl text-slate-800 flex items-center space-x-3">
                    <span>Lesson on {new Date(log.lesson_date).toLocaleDateString()}</span>
                    {log.lesson_time && (
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        {log.lesson_time}
                      </Badge>
                    )}
                    <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                      {log.lesson_hours || 1.0}h
                    </Badge>
                  </CardTitle>
                  <p className="text-xs text-gray-500 mt-1">
                    Log created: {new Date(log.created_at).toLocaleDateString()} at {new Date(log.created_at).toLocaleTimeString()}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge variant="secondary">{log.answers.length} Answers</Badge>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      handleResendEmail(log.id, log.lesson_date);
                    }}
                    title="Resend email to pupil"
                  >
                    <Mail size={16} />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      handlePrintLog(log.id, log.lesson_date);
                    }}
                    title="Print reflection log"
                  >
                    <Printer size={16} />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      handleDownloadPDF(log.id, log.lesson_date);
                    }}
                    title="Download as PDF"
                  >
                    <Download size={16} />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid={`edit-log-${log.id}`}
                    onClick={() => handleEditLog(log)}
                    title="Edit reflection log"
                  >
                    <Edit size={16} />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid={`duplicate-log-${log.id}`}
                    onClick={() => handleDuplicateLog(log)}
                    title="Duplicate log with today's date"
                  >
                    <Copy size={16} />
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDeleteLog(log.id, log.lesson_date)}
                    title="Delete reflection log"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {log.answers.map((answer, index) => (
                  <div key={answer.question_id} className="border-l-4 border-blue-200 pl-4">
                    <h4 className="font-medium text-gray-700 mb-2">
                      Q{index + 1}: {getQuestionText(answer.question_id, answer)}
                    </h4>
                    {renderAnswerDisplay(answer)}
                  </div>
                ))}
                
                {log.notes && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-md">
                    <h4 className="font-medium text-gray-700 mb-1">Instructor Notes</h4>
                    <p className="text-gray-600">{log.notes}</p>
                  </div>
                )}

                {log.attachments && log.attachments.length > 0 && (
                  <div className="mt-4">
                    <h4 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <ImageIcon size={16} /> Photos ({log.attachments.length})
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {log.attachments.map((att) => (
                        <LogImage key={att.id} attachmentId={att.id} filename={att.original_filename} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}

        {logs.length === 0 && (
          <Card className="text-center p-8">
            <CardContent>
              <FileText size={48} className="mx-auto text-green-600 mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No reflection logs yet</h3>
              <p className="text-gray-500 mb-4">No driving lessons have been logged for {pupil.name}</p>
              <Link to="/logs">
                <Button>Create First Reflection Log</Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Edit Reflection Log Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Reflection Log</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-6 max-h-[70vh] overflow-y-auto">
            {/* Voice Note for edit */}
            <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-indigo-800">Voice Note (Optional)</h3>
                  <p className="text-xs text-indigo-600 mt-0.5">Record observations — transcribed into the notes field</p>
                </div>
                <VoiceRecorder onTranscription={(text) => {
                  setEditFormData(prev => ({
                    ...prev,
                    notes: prev.notes ? prev.notes + '\n\n' + text : text
                  }));
                  setEditHasVoiceNote(true);
                  setEditLessonSuggestion('');
                }} />
              </div>

              {/* AI Suggest Next Lesson - appears after voice transcription */}
              {editHasVoiceNote && !editLessonSuggestion && (
                <div className="flex items-center gap-3 pt-2 border-t border-indigo-200">
                  <Button
                    type="button"
                    size="sm"
                    disabled={isEditGeneratingSuggestion || !editFormData.notes.trim()}
                    data-testid="edit-suggest-lesson-btn"
                    onClick={async () => {
                      setIsEditGeneratingSuggestion(true);
                      try {
                        const response = await axios.post(`${API}/voice/suggest-lesson`, {
                          notes: editFormData.notes,
                          pupil_id: pupilId
                        });
                        setEditLessonSuggestion(response.data.suggestion);
                      } catch (error) {
                        const msg = error.response?.data?.detail || error.message;
                        toast.error('Failed to generate suggestion: ' + msg);
                      } finally {
                        setIsEditGeneratingSuggestion(false);
                      }
                    }}
                    className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white"
                  >
                    {isEditGeneratingSuggestion ? (
                      <><Loader2 size={14} className="mr-1.5 animate-spin" /> Generating...</>
                    ) : (
                      <><Sparkles size={14} className="mr-1.5" /> AI Suggest Next Lesson</>
                    )}
                  </Button>
                  <span className="text-xs text-indigo-500">Get an AI-suggested plan based on your notes</span>
                </div>
              )}

              {/* Suggestion Preview */}
              {editLessonSuggestion && (
                <div className="border border-emerald-200 rounded-lg overflow-hidden" data-testid="edit-lesson-suggestion-preview">
                  <div className="bg-emerald-50 px-3 py-2 flex items-center justify-between">
                    <span className="text-sm font-semibold text-emerald-800 flex items-center gap-1.5">
                      <Sparkles size={14} /> Suggested Next Lesson Plan
                    </span>
                    <div className="flex gap-2">
                      <Button type="button" size="sm" data-testid="edit-save-suggestion-btn"
                        onClick={() => {
                          setEditFormData(prev => ({
                            ...prev,
                            notes: prev.notes + '\n\n--- AI Suggested Next Lesson ---\n' + editLessonSuggestion
                          }));
                          setEditLessonSuggestion('');
                          toast.success('Lesson suggestion saved to notes');
                        }}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs">
                        Save to Log
                      </Button>
                      <Button type="button" size="sm" variant="outline" data-testid="edit-discard-suggestion-btn"
                        onClick={() => { setEditLessonSuggestion(''); toast.info('Suggestion discarded'); }}
                        className="h-7 text-xs border-gray-300">
                        Discard
                      </Button>
                    </div>
                  </div>
                  <div className="p-3 text-sm text-gray-700 whitespace-pre-wrap bg-white">{editLessonSuggestion}</div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="edit_lesson_date">Lesson Date *</Label>
                <Input
                  id="edit_lesson_date"
                  type="date"
                  data-testid="edit-lesson-date"
                  value={editFormData.lesson_date}
                  onChange={(e) => setEditFormData({...editFormData, lesson_date: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit_lesson_time">Lesson Time</Label>
                <Input
                  id="edit_lesson_time"
                  type="time"
                  data-testid="edit-lesson-time"
                  value={editFormData.lesson_time}
                  onChange={(e) => setEditFormData({...editFormData, lesson_time: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="edit_lesson_hours">Hours *</Label>
                <Input
                  id="edit_lesson_hours"
                  type="number"
                  step="0.5"
                  min="0.5"
                  max="8"
                  data-testid="edit-lesson-hours"
                  value={editFormData.lesson_hours}
                  onChange={(e) => setEditFormData({...editFormData, lesson_hours: parseFloat(e.target.value)})}
                  required
                />
              </div>
            </div>

            {/* Skills */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-slate-700">Skills & Goals</h3>
              <Textarea
                value={editFormData.skills}
                onChange={(e) => setEditFormData({...editFormData, skills: e.target.value})}
                placeholder="e.g. Roundabouts - approach speed and lane positioning, Mirror checks before signalling..."
                rows={3}
                data-testid="edit-skills-text-input"
              />
            </div>

            {/* Answers */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-700">Reflection Answers</h3>
              {editFormData.answers.map((answer, index) => {
                const question = questions.find(q => q.id === answer.question_id);
                if (!question) return null;
                return (
                  <div key={answer.question_id} className="space-y-2 p-4 border border-gray-200 rounded-lg">
                    <Label className="text-sm font-medium">
                      Q{index + 1}: {question.text}
                    </Label>
                    {question.question_type === 'scale' ? (
                      <div className="space-y-2">
                        <div className="grid grid-cols-10 gap-1">
                          {Array.from({length: question.scale_max - question.scale_min + 1}, (_, i) => question.scale_min + i).map(num => (
                            <button
                              key={num}
                              type="button"
                              onClick={() => handleEditAnswerChange(answer.question_id, num.toString())}
                              className={`h-8 text-xs rounded ${
                                parseInt(answer.answer_text) === num
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                              }`}
                            >
                              {num}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <Textarea
                        value={answer.answer_text}
                        onChange={(e) => handleEditAnswerChange(answer.question_id, e.target.value)}
                        placeholder="Enter your reflection..."
                        rows={3}
                      />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Notes */}
            <div>
              <Label htmlFor="edit_notes">Instructor Notes</Label>
              <Textarea
                id="edit_notes"
                data-testid="edit-notes"
                value={editFormData.notes}
                onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})}
                placeholder="Add instructor notes..."
                rows={4}
              />
            </div>

            {/* Photo Attachments */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-semibold text-slate-700">Photos / Screenshots</Label>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleEditFileUpload}
                    className="hidden"
                    data-testid="edit-photo-upload-input"
                  />
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded-md text-sm font-medium text-slate-700 transition-colors">
                    <Camera size={16} />
                    {isEditUploading ? 'Uploading...' : 'Add Photos'}
                  </span>
                </label>
              </div>
              {editUploadedFiles.length > 0 && (
                <div className="grid grid-cols-3 gap-3">
                  {editUploadedFiles.map((file) => (
                    <div key={file.id} className="relative group rounded-lg overflow-hidden border border-gray-200">
                      {file.previewUrl ? (
                        <img src={file.previewUrl} alt={file.filename} className="w-full h-24 object-cover" />
                      ) : (
                        <EditLogImage attachmentId={file.id} />
                      )}
                      <button
                        type="button"
                        onClick={() => removeEditFile(file.id)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={14} />
                      </button>
                      <p className="text-xs text-gray-500 truncate px-2 py-1">{file.filename}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Button type="submit" data-testid="save-edit-log" className="w-full">Save Changes</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};


export default PupilLogsPage;
